import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { CaseTagSelector } from "../../cases/CaseTagSelector";
import {
  createCompanyWebhook,
  deleteCompanyWebhook,
  fetchCompanyWebhookDeliveries,
  fetchCompanyWebhooks,
  rollCompanyWebhookKey,
  testCompanyWebhook,
  updateCompanyWebhook,
} from "../../../../api/companyWebhooks";
import {
  applyCreatedHook,
  applyLabelIds,
  applyModuleToggle,
  applyStatus,
  createWebhookPatchQueue,
  mergeOmittedIds,
  mergeRemoteHooks,
  removeHookFromList,
  toggleModule,
  unionDirtyFields,
} from "./webhookEditorState";

const FALLBACK_CATALOG = {
  inbound: ["customer", "contact", "case"],
  outbound: ["customer", "contact"],
  coming_soon: ["project", "order", "report"],
};

const emptyForm = (direction = "inbound", catalog = FALLBACK_CATALOG) => ({
  name: "",
  provider: "generic",
  direction,
  target_url: "",
  modules: [...(catalog[direction] || FALLBACK_CATALOG[direction])],
  label_ids: [],
});

function ModuleChecks({ direction, value, catalog, onChange, disabled }) {
  const { t } = useTranslation();
  const supported = catalog[direction] || [];
  const comingSoon = catalog.coming_soon || [];

  return (
    <fieldset className="sm:col-span-2">
      <legend className="text-sm font-semibold text-slate-700">{t("company.webhooks.modules_label")}</legend>
      <p className="mt-1 text-xs text-slate-500">{t("company.webhooks.modules_help")}</p>
      <div className="mt-2 flex flex-wrap gap-3">
        {supported.map((mod) => (
          <label key={mod} className="inline-flex items-center gap-2 text-sm text-slate-800">
            <input
              type="checkbox"
              className="rounded border-slate-300"
              checked={value.includes(mod)}
              disabled={disabled}
              onChange={() => onChange(mod)}
            />
            {t(`company.webhooks.module.${mod}`)}
          </label>
        ))}
        {comingSoon.map((mod) => (
          <label key={mod} className="inline-flex items-center gap-2 text-sm text-slate-400">
            <input type="checkbox" className="rounded border-slate-200" disabled />
            {t(`company.webhooks.module.${mod}`)}
            <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide">
              {t("company.webhooks.coming_soon")}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export default function WebhooksTab() {
  const { t } = useTranslation();
  const [hooks, setHooks] = useState([]);
  const [catalog, setCatalog] = useState(FALLBACK_CATALOG);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm());
  const [revealed, setRevealed] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const hooksRef = useRef(hooks);
  hooksRef.current = hooks;
  const loadRef = useRef(() => {});
  const patchQueueRef = useRef(null);
  if (!patchQueueRef.current) {
    patchQueueRef.current = createWebhookPatchQueue({
      update: updateCompanyWebhook,
      onError: () => {
        toast.error(t("company.webhooks.update_failed"));
        loadRef.current({ silent: true });
      },
    });
  }

  const commitHooks = (next) => {
    hooksRef.current = next;
    setHooks(next);
  };

  const load = (opts = {}) => {
    if (!opts.silent) setLoading(true);
    const dirtyAtStart = patchQueueRef.current.dirtyFields();
    const omittedAtStart = patchQueueRef.current.discardedIds();
    fetchCompanyWebhooks()
      .then((res) => {
        const next = mergeRemoteHooks(
          res.data || [],
          hooksRef.current,
          unionDirtyFields(dirtyAtStart, patchQueueRef.current.dirtyFields()),
          mergeOmittedIds(omittedAtStart, patchQueueRef.current.discardedIds())
        );
        commitHooks(next);
        if (res.meta?.modules) setCatalog({ ...FALLBACK_CATALOG, ...res.meta.modules });
      })
      .catch(() => toast.error(t("company.webhooks.load_failed")))
      .finally(() => {
        if (!opts.silent) setLoading(false);
      });
  };
  loadRef.current = load;

  useEffect(() => {
    load();
  }, []);

  const copy = (value) => {
    navigator.clipboard.writeText(value);
    toast.success(t("company.integrations.copied"));
  };

  const handleDirection = (direction) => {
    setForm((prev) => ({
      ...prev,
      direction,
      modules: [...(catalog[direction] || FALLBACK_CATALOG[direction])],
      target_url: direction === "outbound" ? prev.target_url : "",
    }));
  };

  const handleCreate = (e) => {
    e.preventDefault();
    createCompanyWebhook(form)
      .then((res) => {
        setRevealed(res.data);
        commitHooks(applyCreatedHook(hooksRef.current, { ...form, ...(res.data || {}) }));
        setForm(emptyForm(form.direction, catalog));
        toast.success(t("company.webhooks.created"));
      })
      .catch((err) => toast.error(err?.response?.data?.message || t("company.webhooks.create_failed")));
  };

  const handleRoll = (hook) => {
    rollCompanyWebhookKey(hook.id)
      .then((res) => {
        setRevealed(res.data);
        toast.success(t("company.webhooks.rolled"));
      })
      .catch((err) => toast.error(err?.response?.data?.message || t("company.webhooks.roll_failed")));
  };

  const handleToggle = (hook) => {
    const status = hook.status === "active" ? "disabled" : "active";
    const next = applyStatus(hooksRef.current, hook.id, status);
    if (next.rejected) return;
    commitHooks(next.hooks);
    patchQueueRef.current.enqueue(hook.id, { status });
  };

  const handleLabels = (hookId, label_ids) => {
    const next = applyLabelIds(hooksRef.current, hookId, label_ids);
    if (next.rejected) return;
    commitHooks(next.hooks);
    patchQueueRef.current.enqueue(hookId, { label_ids: next.label_ids });
  };

  const handleModules = (hookId, moduleName) => {
    const next = applyModuleToggle(hooksRef.current, hookId, moduleName);
    if (next.rejected === "blank") {
      toast.error(t("company.webhooks.modules_blank"));
      return;
    }
    if (next.rejected) return;
    commitHooks(next.hooks);
    patchQueueRef.current.enqueue(hookId, { modules: next.modules });
  };

  const handleDelete = (hook) => {
    deleteCompanyWebhook(hook.id)
      .then(() => {
        toast.success(t("company.webhooks.deleted"));
        patchQueueRef.current.drop(hook.id);
        commitHooks(removeHookFromList(hooksRef.current, hook.id));
        if (selectedId === hook.id) {
          setSelectedId(null);
          setDeliveries([]);
        }
      })
      .catch(() => toast.error(t("company.webhooks.delete_failed")));
  };

  const handleTest = (hook) => {
    testCompanyWebhook(hook.id)
      .then((res) => toast.success(res.data?.message || t("company.webhooks.test_queued")))
      .catch(() => toast.error(t("company.webhooks.test_failed")));
  };

  const openDeliveries = (hook) => {
    setSelectedId(hook.id);
    fetchCompanyWebhookDeliveries(hook.id)
      .then((res) => setDeliveries(res.data || []))
      .catch(() => toast.error(t("company.webhooks.deliveries_failed")));
  };

  const supportedSummary = useMemo(
    () => ({
      inbound: (catalog.inbound || []).map((mod) => t(`company.webhooks.module.${mod}`)).join(", "),
      outbound: (catalog.outbound || []).map((mod) => t(`company.webhooks.module.${mod}`)).join(", "),
      soon: (catalog.coming_soon || []).map((mod) => t(`company.webhooks.module.${mod}`)).join(", "),
    }),
    [catalog, t]
  );

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="text-base font-extrabold text-slate-950">{t("company.webhooks.guide_title")}</h3>
        <p className="mt-2 text-sm text-slate-600">{t("company.webhooks.guide_intro")}</p>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="rounded-xl bg-[#F8FAFC] p-4">
            <p className="text-xs font-bold uppercase tracking-wide text-[#1463C7]">{t("company.webhooks.inbound")}</p>
            <p className="mt-1 text-sm text-slate-700">{t("company.webhooks.guide_inbound")}</p>
            <p className="mt-2 text-sm text-slate-700">{t("company.webhooks.custom_fields_help")}</p>
            <p className="mt-2 text-xs text-slate-500">
              {t("company.webhooks.supported_now")}: {supportedSummary.inbound}
            </p>
          </div>
          <div className="rounded-xl bg-[#F8FAFC] p-4">
            <p className="text-xs font-bold uppercase tracking-wide text-[#1463C7]">{t("company.webhooks.outbound")}</p>
            <p className="mt-1 text-sm text-slate-700">{t("company.webhooks.guide_outbound")}</p>
            <p className="mt-2 text-xs text-slate-500">
              {t("company.webhooks.supported_now")}: {supportedSummary.outbound}
            </p>
          </div>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          {t("company.webhooks.coming_soon")}: {supportedSummary.soon}
        </p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-5">
        <h3 className="text-base font-extrabold text-slate-950">{t("company.webhooks.connect_title")}</h3>
        <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-slate-600">
          <li>{t("company.webhooks.connect_step1")}</li>
          <li>{t("company.webhooks.connect_step2")}</li>
          <li>{t("company.webhooks.connect_step3")}</li>
          <li>{t("company.webhooks.connect_step4")}</li>
        </ol>
      </div>

      {revealed && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">{t("company.webhooks.secret_once")}</p>
          {revealed.inbound_url && (
            <p className="mt-2 font-mono text-xs break-all">
              URL: {revealed.inbound_url}{" "}
              <button type="button" className="text-[#1463C7]" onClick={() => copy(revealed.inbound_url)}>
                {t("company.integrations.copy")}
              </button>
            </p>
          )}
          {revealed.secret && (
            <p className="mt-1 font-mono text-xs break-all">
              {t("company.webhooks.header")}: X-Promang-Webhook-Secret = {revealed.secret}{" "}
              <button type="button" className="text-[#1463C7]" onClick={() => copy(revealed.secret)}>
                {t("company.integrations.copy")}
              </button>
            </p>
          )}
        </div>
      )}

      <form onSubmit={handleCreate} className="rounded-[18px] border border-slate-200 bg-white p-5">
        <h3 className="text-base font-extrabold text-slate-950">{t("company.webhooks.new")}</h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className="text-sm font-semibold text-slate-700">
            {t("company.webhooks.name")}
            <input
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </label>
          <label className="text-sm font-semibold text-slate-700">
            {t("company.webhooks.provider")}
            <select
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
              value={form.provider}
              onChange={(e) => setForm({ ...form, provider: e.target.value })}
            >
              <option value="generic">{t("company.webhooks.generic")}</option>
              <option value="weply">{t("company.webhooks.provider_lead")}</option>
            </select>
          </label>
          <label className="text-sm font-semibold text-slate-700">
            {t("company.webhooks.direction")}
            <select
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
              value={form.direction}
              onChange={(e) => handleDirection(e.target.value)}
            >
              <option value="inbound">{t("company.webhooks.inbound")}</option>
              <option value="outbound">{t("company.webhooks.outbound")}</option>
            </select>
          </label>
          {form.direction === "outbound" && (
            <label className="text-sm font-semibold text-slate-700 sm:col-span-2">
              {t("company.webhooks.target_url")}
              <input
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
                value={form.target_url}
                onChange={(e) => setForm({ ...form, target_url: e.target.value })}
                required
              />
            </label>
          )}
          <ModuleChecks
            direction={form.direction}
            value={form.modules}
            catalog={catalog}
            onChange={(mod) =>
              setForm((prev) => ({
                ...prev,
                modules: toggleModule(prev.modules, mod, prev.direction),
              }))
            }
          />
          {form.direction === "inbound" && (
            <div className="sm:col-span-2">
              <p className="text-sm font-semibold text-slate-700">{t("company.webhooks.labels_label")}</p>
              <p className="mt-1 text-xs text-slate-500">
                {t("company.webhooks.labels_help")}{" "}
                <Link to="/dashboard/statistics#tag-definitions" className="font-semibold text-[#1463C7]">
                  {t("company.webhooks.labels_manage")}
                </Link>
              </p>
              <div className="mt-2">
                <CaseTagSelector
                  value={form.label_ids}
                  onChange={(label_ids) => setForm({ ...form, label_ids })}
                  scopeModule="case"
                  variant="inline"
                  buttonLabel={t("company.webhooks.labels_pick")}
                />
              </div>
            </div>
          )}
        </div>
        <button type="submit" className="mt-4 rounded-xl bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white">
          {t("company.webhooks.create")}
        </button>
      </form>

      <div className="overflow-hidden rounded-[18px] border border-slate-200 bg-white">
        {loading ? (
          <p className="p-5 text-sm text-slate-500">{t("Loading")}</p>
        ) : hooks.length === 0 ? (
          <p className="p-5 text-sm text-slate-500">{t("company.webhooks.empty")}</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {hooks.map((hook) => (
              <li key={hook.id} className="p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="font-semibold text-slate-950">{hook.name}</p>
                    <p className="text-xs text-slate-500">
                      {hook.provider === "weply" ? t("company.webhooks.provider_lead") : t("company.webhooks.generic")} · {hook.direction} · {hook.status}
                      {hook.last_status ? ` · ${hook.last_status}` : ""}
                    </p>
                    {hook.inbound_url && (
                      <p className="mt-1 font-mono text-xs break-all text-slate-500">{hook.inbound_url}</p>
                    )}
                    <div className="mt-3">
                      <ModuleChecks
                        direction={hook.direction}
                        value={hook.modules || []}
                        catalog={catalog}
                        onChange={(mod) => handleModules(hook.id, mod)}
                      />
                    </div>
                    {hook.direction === "inbound" && (
                      <div className="mt-3">
                        <p className="text-sm font-semibold text-slate-700">{t("company.webhooks.labels_label")}</p>
                        <p className="mt-1 text-xs text-slate-500">{t("company.webhooks.labels_help")}</p>
                        <div className="mt-2">
                          <CaseTagSelector
                            value={hook.label_ids || []}
                            onChange={(label_ids) => handleLabels(hook.id, label_ids)}
                            scopeModule="case"
                            variant="inline"
                            buttonLabel={t("company.webhooks.labels_pick")}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" className="rounded-lg border px-3 py-1.5 text-xs font-semibold" onClick={() => handleRoll(hook)}>
                      {t("company.webhooks.roll")}
                    </button>
                    <button type="button" className="rounded-lg border px-3 py-1.5 text-xs font-semibold" onClick={() => handleToggle(hook)}>
                      {hook.status === "active" ? t("company.webhooks.disable") : t("company.webhooks.enable")}
                    </button>
                    <button type="button" className="rounded-lg border px-3 py-1.5 text-xs font-semibold" onClick={() => handleTest(hook)}>
                      {t("company.webhooks.test")}
                    </button>
                    <button type="button" className="rounded-lg border px-3 py-1.5 text-xs font-semibold" onClick={() => openDeliveries(hook)}>
                      {t("company.webhooks.log")}
                    </button>
                    <button type="button" className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white" onClick={() => handleDelete(hook)}>
                      {t("company.webhooks.delete")}
                    </button>
                  </div>
                </div>
                {selectedId === hook.id && deliveries.length > 0 && (
                  <ul className="mt-3 space-y-2 rounded-xl bg-slate-50 p-3 text-xs">
                    {deliveries.map((row) => (
                      <li key={row.id}>
                        {row.created_at} · {row.event} · {row.status} · {row.http_status || "-"}
                        {row.error_message ? ` · ${row.error_message}` : ""}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
